/**
 * useCourses Hook
 */

import { useState, useEffect, useCallback } from 'react';
import { getAllCourses } from '../../services/coursesService';
import type { Course, CourseAvailability } from '../../types/courses.types';
import { parseLocalDate } from '../../utils/date';

interface UseCoursesResult {
  isLoading: boolean;
  error: string | null;
  courses: Course[];
  refresh: () => void;
}

export function getCourseAvailability(course: Course): CourseAvailability {
  const courseDate = parseLocalDate(course.courseDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (courseDate < today) return 'past';
  if (course.maxParticipants === null) return 'available';
  if (course.enrolledCount >= course.maxParticipants) return 'full';

  const spotsRemaining = course.maxParticipants - course.enrolledCount;
  if (spotsRemaining <= 3) return 'almost_full';

  return 'available';
}

export function getSpotsRemainingText(course: Course): string {
  if (course.maxParticipants === null) return 'Ubegrænset';
  const remaining = course.maxParticipants - course.enrolledCount;
  if (remaining <= 0) return 'Ingen pladser';
  if (remaining === 1) return '1 plads tilbage';
  return `${remaining} pladser tilbage`;
}

export function useCourses(
  employeeId: number | null,
  isAuthenticated: boolean,
  includePast = false
): UseCoursesResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);

  const fetchCourses = useCallback(async () => {
    if (!employeeId || !isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await getAllCourses(includePast);
      setCourses(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Der opstod en fejl');
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, isAuthenticated, includePast]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  return { isLoading, error, courses, refresh: fetchCourses };
}
